import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'
import { Link } from 'react-router-dom'
import { Star, Calendar, Plane } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useFeaturedPackages } from '../hooks/useFeaturedPackages'
import { publicUrl } from '../utils/publicUrl'

export default function About() {
  const { franchiseData } = useFranchiseData()
  const { featuredPackages, loading: packagesLoading, error: packagesError } = useFeaturedPackages()

  // Helper function similar to Packages.tsx
  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) {
      return pkg.cover_photo.large_url
    }
    if (pkg.cover_photo?.url) {
      return pkg.cover_photo.url
    }
    if (pkg.scraped_cover_photo) {
      return pkg.scraped_cover_photo
    }
    return null
  }

  return (
    <div className="pt-20 sm:pt-28 pb-0 bg-indojet-light min-h-screen">
      <Helmet>
        <title>About Us | IndoJet Travel</title>
        <meta
          name="description"
          content="Discover the legacy of IndoJet Travel Sandton, a trusted partner delivering bespoke corporate and leisure journeys worldwide."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/about' : '/about'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Us | IndoJet Travel" />
        <meta
          property="og:description"
          content="Learn more about IndoJet Travel Sandton’s international footprint and Discovery partnership."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/about'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Us',
            url: typeof window !== 'undefined' ? window.location.href : '/about',
            description:
              'IndoJet Travel Sandton delivers bespoke travel solutions for corporate and leisure travellers.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'About',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/about',
              },
            ],
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8 space-y-24">
        {/* Heritage Section */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="rounded-2xl overflow-hidden shadow-2xl h-fit">
            <img
              src={publicUrl('indojectF.jpg')}
              alt="Indo Jet Travel activation"
              className="w-full h-auto object-cover max-h-[550px]"
            />
          </div>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-sm uppercase tracking-[0.4em] text-[#182c78] font-semibold">
              Why Us
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              A Legacy of Global Travel Leadership
            </h1>
            <p>
              Indo Jet Travel Sandton has a storied legacy of global operations, spanning back to its
              establishment in 1967. In 1990, when Sharon Seskin assumed ownership of the business, the agency
              embarked on a transformative journey that significantly expanded its international reach.
            </p>
            <p>
              Fast forward to 2011, under her visionary leadership, Indo Jet Travel Sandton proudly opened an
              office in Sydney, Australia, marking a pivotal moment in the company's history. This strategic move
              has propelled us into a more globally connected era, providing us with expanded access to diverse and
              dynamic international markets.
            </p>
            <p>
              At Indo Jet Travel Sandton, our commitment to delivering exceptional travel solutions resonates with
              the comprehensive scope of services you are seeking. We recognize that travel needs span an array of
              dimensions, and we are poised to address each one with dedication and expertise.
            </p>
          </div>
        </section>

        {/* Mohau Equity Partners Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex justify-center mb-8">
            <img
              src={publicUrl('Mohau-EP-logo-FINAL-01-1024x329-removebg-preview (1).png')}
              alt="Mohau Equity Partners"
              className="max-w-xs sm:max-w-md w-full"
            />
          </div>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              In a significant development in 2021, through Discovery's strategic partner, Mohau Equity Partners
              (MEP) acquired 70% equity into Indo Jet Travel. In five years Mohau has invested in 12 companies that
              are part of the Discovery supply chain ecosystem.
            </p>
            <p>
              Committed and deployed close to R300m of Discovery empowerment capital, the partnership is about
              business platforms that optimise costs as well as provide transparent and collaborative value chains
              under Discovery's influence. In 2009 travel was identified as a long-term strategic value enabler for
              Discovery, culminating in the merger of the Discovery travel team and Indo Jet into one team based at
              Discovery Place.
            </p>
            <p>
              A collaborative business model has emerged that goes above the supplier relationship business model,
              ensuring Indo Jet Travel remains one of Discovery's longest-standing travel partners.
            </p>
          </div>
        </section>

      </div>

      {/* Featured Packages section */}
      <section id="offers" className="pt-16 sm:pt-20 pb-16 sm:pb-20 bg-white w-full mt-16 sm:mt-24">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
            {/* Section Header */}
            <div className="text-center mb-12">
              <p className="text-[#182c78] text-xs font-semibold uppercase tracking-wider mb-3">
                OUR ACCOMMODATIONS
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Featured Packages
              </h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Discover our most popular accommodation options, each offering a perfect blend of comfort, style, and breathtaking sea views.
              </p>
            </div>

            {packagesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                    <div className="aspect-[4/3] bg-gray-300"></div>
                    <div className="p-6">
                      <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                      <div className="h-20 bg-gray-300 rounded mb-4"></div>
                      <div className="flex gap-4 mb-4">
                        <div className="h-4 bg-gray-300 rounded w-16"></div>
                        <div className="h-4 bg-gray-300 rounded w-16"></div>
                        <div className="h-4 bg-gray-300 rounded w-16"></div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="h-8 bg-gray-300 rounded w-20"></div>
                        <div className="h-10 bg-gray-300 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : packagesError ? (
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">Unable to load packages</h3>
                <p className="mt-2 text-sm text-gray-600">{packagesError}</p>
                <button onClick={() => window.location.reload()} className="mt-4 btn-primary">
                  Try Again
                </button>
              </div>
            ) : featuredPackages.length === 0 ? (
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  No featured packages available
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Check back soon for our latest travel deals.
                </p>
                <a href="/packages" className="mt-4 btn-primary">
                  View All Packages
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredPackages.slice(0, 3).map((pkg) => {
                  const imageUrl = getImageUrl(pkg)

                  return (
                    <div key={pkg.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
                      {/* Image Section */}
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <div
                          className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-300"
                          style={{
                            backgroundColor: franchiseData?.accent_color || '#182c78',
                            backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                          }}
                        >
                          {!imageUrl && (
                            <div className="flex items-center justify-center h-full">
                              <Plane className="h-12 w-12 text-white opacity-60" />
                            </div>
                          )}
                        </div>
                        
                        {/* Night Badge */}
                        {pkg.nights && (
                          <div className="absolute top-4 right-4 bg-[#182c78] text-white px-3 py-1 rounded-full text-sm font-semibold">
                            {pkg.nights} Night{pkg.nights !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>

                      {/* Content Section */}
                      <div className="p-4">
                        {/* Title */}
                        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                          {pkg.name}
                        </h3>
                        
                        {/* Location */}
                        <div className="flex items-center text-gray-600 mb-3">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 21l-4.243-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm">{pkg.category || 'International'}</span>
                        </div>

                        {/* Description */}
                        <p className="text-gray-600 text-xs line-clamp-3 mb-3">
                          {pkg.intro || pkg.description || 'Experience luxury and comfort with breathtaking views and world-class amenities.'}
                        </p>

                        {/* Read More Link */}
                        <button className="text-[#182c78] text-xs font-medium mb-3 hover:underline">
                          Read more
                        </button>

                        {/* Rating and Date */}
                        <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            {Array.from({ length: pkg.rating || 5 }).map((_, i) => (
                              <Star key={i} className="h-4 w-4 fill-[#182c78] text-[#182c78]" />
                            ))}
                            <span className="ml-1">{pkg.rating || '5.0'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>{pkg.dates || '01 Sept 2025 - 31 Oct 2025'}</span>
                          </div>
                        </div>

                        {/* Amenities */}
                        <div className="flex items-center gap-4 mb-4 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                            </svg>
                            <span>Wi-Fi</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <span>Kitchen</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                            <span>Bathroom</span>
                          </div>
                        </div>

                        {/* Price and Button */}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xl font-bold text-gray-900">
                              {pkg.formatted_price || 'R21,680'}
                            </span>
                          </div>
                          <Link 
                            to={`/packages/${pkg.slug}`}
                            className="bg-[#1e40af] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#1e40af]/90 transition-colors flex items-center gap-2 text-sm"
                          >
                            View Details
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* View All Packages Button */}
            <div className="text-center mt-8 mb-0 pb-0">
              <Link 
                to="/packages"
                className="inline-flex items-center px-6 py-2 bg-[#1e40af] text-white font-semibold rounded-lg hover:bg-[#1e40af]/90 transition-colors gap-2 text-sm"
              >
                View All Packages
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
    </div>
  )
}
